import { createSHA256 } from 'hash-wasm'

import type { MediaKind } from '@entities/media'

export type UploadProgress = {
	loaded: number
	total: number
	percent: number
}

export type UploadAttempt = {
	version: 1
	fingerprint: string
	kind: MediaKind
	filename: string
	contentType: string
	size: number
	lastModified: number
	createIdempotencyKey: string
	completeIdempotencyKey: string
	checksum?: string
	session?: {
		assetId: string
		uploadUrl: string
		expiresAt: string
		headers: Record<string, string>
	}
	uploadEtag?: string
	completed?: boolean
}

const uploadAttemptStorageKey = 'tisit.admin.media.upload-attempt.v1'

const browserStorage = (): Storage | null => typeof window === 'undefined' ? null : window.localStorage

export const uploadFileFingerprint = (file: Pick<File, 'name' | 'size' | 'type' | 'lastModified'>, kind: MediaKind) =>
	[kind, file.name, file.size, file.type, file.lastModified].join('\u0000')

export const createUploadAttempt = (
	file: Pick<File, 'name' | 'size' | 'type' | 'lastModified'>,
	kind: MediaKind,
	createKey: string = crypto.randomUUID(),
	completeKey: string = crypto.randomUUID(),
): UploadAttempt => ({
	version: 1,
	fingerprint: uploadFileFingerprint(file, kind),
	kind,
	filename: file.name,
	contentType: file.type,
	size: file.size,
	lastModified: file.lastModified,
	createIdempotencyKey: createKey,
	completeIdempotencyKey: completeKey,
})

export const uploadAttemptMatches = (
	attempt: UploadAttempt | null,
	file: Pick<File, 'name' | 'size' | 'type' | 'lastModified'>,
	kind: MediaKind,
) => Boolean(attempt && attempt.fingerprint === uploadFileFingerprint(file, kind))

export const isUploadSessionFresh = (attempt: UploadAttempt, now = Date.now()) =>
	Boolean(attempt.session && Date.parse(attempt.session.expiresAt) > now + 5_000)

export const loadUploadAttempt = (storage: Storage | null = browserStorage()): UploadAttempt | null => {
	if (!storage) return null
	try {
		const value = JSON.parse(storage.getItem(uploadAttemptStorageKey) ?? 'null') as UploadAttempt | null
		if (!value || value.version !== 1 || !value.fingerprint || !value.createIdempotencyKey || !value.completeIdempotencyKey) return null
		return value
	} catch {
		return null
	}
}

export const saveUploadAttempt = (attempt: UploadAttempt, storage: Storage | null = browserStorage()) => {
	storage?.setItem(uploadAttemptStorageKey, JSON.stringify(attempt))
	return attempt
}

export const clearUploadAttempt = (storage: Storage | null = browserStorage()) =>
	storage?.removeItem(uploadAttemptStorageKey)

const throwIfAborted = (signal?: AbortSignal) => {
	if (signal?.aborted) throw new DOMException('Операция отменена', 'AbortError')
}

const readBlob = (blob: Blob) => {
	if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer()
	return new Promise<ArrayBuffer>((resolve, reject) => {
		const reader = new FileReader()
		reader.onerror = () => reject(reader.error ?? new Error('Unable to read file chunk'))
		reader.onload = () => resolve(reader.result as ArrayBuffer)
		reader.readAsArrayBuffer(blob)
	})
}

export const sha256File = async (
	file: File,
	options: {
		signal?: AbortSignal
		onProgress?: (progress: UploadProgress) => void
		chunkSize?: number
	} = {},
) => {
	const chunkSize = options.chunkSize ?? 4 * 1024 * 1024
	const hasher = await createSHA256()
	hasher.init()
	let loaded = 0
	while (loaded < file.size) {
		throwIfAborted(options.signal)
		const end = Math.min(loaded + chunkSize, file.size)
		const chunk = new Uint8Array(await readBlob(file.slice(loaded, end)))
		throwIfAborted(options.signal)
		hasher.update(chunk)
		loaded = end
		options.onProgress?.({ loaded, total: file.size, percent: file.size ? Math.round(loaded / file.size * 100) : 100 })
	}
	throwIfAborted(options.signal)
	return hasher.digest('hex')
}

export const putFile = ({
	file,
	url,
	headers,
	signal,
	onProgress,
}: {
	file: File
	url: string
	headers: Record<string, string>
	signal?: AbortSignal
	onProgress?: (progress: UploadProgress) => void
}) => new Promise<{ etag: string }>((resolve, reject) => {
	const xhr = new XMLHttpRequest()
	const abort = () => xhr.abort()
	const cleanup = () => signal?.removeEventListener('abort', abort)
	if (signal?.aborted) {
		reject(new DOMException('Операция отменена', 'AbortError'))
		return
	}
	signal?.addEventListener('abort', abort, { once: true })
	xhr.open('PUT', url)
	Object.entries(headers).forEach(([name, value]) => xhr.setRequestHeader(name, value))
	xhr.upload.onprogress = event => {
		const total = event.lengthComputable ? event.total : file.size
		onProgress?.({ loaded: event.loaded, total, percent: total ? Math.round(event.loaded / total * 100) : 0 })
	}
	xhr.onerror = () => {
		cleanup()
		reject(new Error('Object storage rejected the upload request'))
	}
	xhr.onabort = () => {
		cleanup()
		reject(new DOMException('Операция отменена', 'AbortError'))
	}
	xhr.onload = () => {
		cleanup()
		if (xhr.status < 200 || xhr.status >= 300) {
			reject(new Error(`Object storage returned HTTP ${xhr.status}`))
			return
		}
		const etag = xhr.getResponseHeader('ETag')?.replaceAll('"', '') ?? ''
		if (!etag) {
			reject(new Error('Object storage did not expose the ETag response header'))
			return
		}
		resolve({ etag })
	}
	xhr.send(file)
})
